import React, { useEffect } from "react";
import {
    Box,
    BoxProps,
    Button,
    Card,
    Grid,
    TextField,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Styles from "pages/external/profile/profile.styles";
import * as userService from "services/identityUser-service";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuthContext } from "contexts/authContext";

interface IProps {
    showMessage: MessageBox;
}

const Profile = (props: IProps) => {
    const { user, checkAuth, updateUserNames } = useAuthContext();
    const { showMessage } = props;
    const navigate = useNavigate();
    const theme = useTheme();
    const { t } = useTranslation();
    const [searchParams, _] = useSearchParams();
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [errorProfile, setErrorProfile] = React.useState<boolean>(false);
    const [errorPassword, setErrorPassword] = React.useState<boolean>(false);
    const isMobile = useMediaQuery(theme.breakpoints.between("xs", "md"));

    useEffect(() => {}, []);

    const Content = () => {
        const SCHEMAPASSWORD = Yup.object({
            oldPassword: Yup.string().required(t("yup.required")),
            password: Yup.string()
                .required(t("yup.required"))
                .min(8, t("yup.min8"))
                .matches(/(?=.*[0-9])/, t("yup.number"))
                .matches(/(?=.*[a-z])/, t("yup.lowercase"))
                .matches(/(?=.*[A-Z])/, t("yup.uppercase"))
                .matches(/(?=.*[!@#$%^&*])/, t("yup.special")),
            testPassword: Yup.string()
                .required(t("yup.required"))
                .oneOf([Yup.ref("password")], t("yup.match")),
        });
        const formikPassword = useFormik({
            initialValues: {
                oldPassword: "",
                password: "",
                testPassword: "",
            },
            validationSchema: SCHEMAPASSWORD,
            onSubmit: (values) => {},
        });

        const SCHEMAPROFILE = Yup.object({
            firstName: Yup.string().required(t("yup.required")),
            lastName: Yup.string().required(t("yup.required")),
        });
        const formikProfile = useFormik({
            initialValues: {
                firstName: user != null ? user?.given_name : "",
                lastName: user != null ? user?.surname : "",
            },
            validationSchema: SCHEMAPROFILE,
            onSubmit: (values) => {},
        });

        const handleSubmitProfile = (): void => {
            setErrorMessage("");
            setErrorProfile(false);
            userService
                .updateUserProfile({
                    name: formikProfile.values.firstName,
                    surname: formikProfile.values.lastName,
                })
                .then(() => {
                    showMessage(t("profile.success"), "success", 5000);
                    updateUserNames(
                        formikProfile.values.firstName,
                        formikProfile.values.lastName,
                    );
                })
                .catch((err) => {
                    setErrorMessage(t("errors.500"));
                    setErrorProfile(true);
                });
        };

        const handleSubmitPassword = (): void => {
            setErrorMessage("");
            setErrorPassword(false);
            userService
                .changeUserPassword({
                    oldPassword: formikPassword.values.oldPassword,
                    newPassword: formikPassword.values.password,
                })
                .then(() => {
                    showMessage(t("profile.success"), "success", 5000);
                    formikPassword.resetForm();
                })
                .catch((err) => {
                    setErrorMessage(t("errors.500"));
                    setErrorPassword(true);
                });
        };

        const infoTextProps: BoxProps = {
            sx: Styles.infotext(theme),
        };

        return (
            <Box sx={Styles.containerCSS(theme)}>
                <h1>{t("profile.title")}</h1>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            value={user != null ? user.email : ""}
                            fullWidth
                            label={t("profile.labels.email")}
                            name="email"
                            disabled
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            onChange={formikProfile.handleChange}
                            label={t("profile.labels.name")}
                            value={formikProfile.values.firstName}
                            error={
                                formikProfile.touched.firstName &&
                                Boolean(formikProfile.errors.firstName)
                            }
                            helperText={
                                formikProfile.touched.firstName &&
                                formikProfile.errors.firstName
                            }
                            name="firstName"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            onChange={formikProfile.handleChange}
                            label={t("profile.labels.surname")}
                            value={formikProfile.values.lastName}
                            error={
                                formikProfile.touched.lastName &&
                                Boolean(formikProfile.errors.lastName)
                            }
                            helperText={
                                formikProfile.touched.lastName &&
                                formikProfile.errors.lastName
                            }
                            name="lastName"
                            fullWidth
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sx={{
                            paddingBottom: "1.25rem",
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleSubmitProfile}
                            disabled={
                                !(formikProfile.isValid && formikProfile.dirty)
                            }
                            fullWidth
                            color="primary"
                        >
                            {t("profile.save")}
                        </Button>
                        {errorProfile && (
                            <Box color="error.main">
                                <Box>{errorMessage}</Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>

                <h1>{t("profile.titlePassword")}</h1>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label={t("profile.labelsPassword.oldPassword")}
                            onChange={formikPassword.handleChange}
                            onBlur={formikPassword.handleBlur}
                            value={formikPassword.values.oldPassword}
                            error={
                                formikPassword.touched.oldPassword &&
                                Boolean(formikPassword.errors.oldPassword)
                            }
                            helperText={
                                formikPassword.touched.oldPassword &&
                                formikPassword.errors.oldPassword
                            }
                            fullWidth
                            type="password"
                            name="oldPassword"
                            autoComplete="current-password"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={t("profile.labelsPassword.newPassword")}
                            onChange={formikPassword.handleChange}
                            onBlur={formikPassword.handleBlur}
                            value={formikPassword.values.password}
                            error={
                                formikPassword.touched.password &&
                                Boolean(formikPassword.errors.password)
                            }
                            helperText={
                                formikPassword.touched.password &&
                                formikPassword.errors.password
                            }
                            fullWidth
                            type="password"
                            name="password"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={t(
                                "profile.labelsPassword.newPasswordConfirmation",
                            )}
                            // value={testPassword}
                            // error={error.length > 0}
                            // helperText={error}
                            // onChange={(e) => setTestPassword(e.target.value)}
                            onChange={formikPassword.handleChange}
                            onBlur={formikPassword.handleBlur}
                            value={formikPassword.values.testPassword}
                            error={
                                formikPassword.touched.testPassword &&
                                Boolean(formikPassword.errors.testPassword)
                            }
                            helperText={
                                formikPassword.touched.testPassword &&
                                formikPassword.errors.testPassword
                            }
                            fullWidth
                            type="password"
                            name="testPassword"
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ paddingBottom: "1.25rem" }}>
                        <Button
                            variant="contained"
                            onClick={handleSubmitPassword}
                            disabled={
                                !(
                                    formikPassword.isValid &&
                                    formikPassword.dirty
                                )
                            }
                            fullWidth
                            color="primary"
                        >
                            {t("profile.save")}
                        </Button>
                        {errorPassword && (
                            <Box color="error.main">
                                <Box>{errorMessage}</Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Box>
        );
    };

    return (
        <Box maxWidth="lg" sx={Styles.wrapper(theme)}>
            {isMobile ? (
                <Box>
                    <Box sx={Styles.containerCSS(theme)}>
                        <h1>Complete Login</h1>
                        <Box sx={Styles.infotext(theme)}>
                            <Content />
                        </Box>
                    </Box>
                </Box>
            ) : (
                <Card sx={Styles.cardCSS(theme)}>
                    <Content />
                </Card>
            )}
        </Box>
    );
};

export default Profile;
